import { useEffect } from 'react';
import { useAvailableTargets } from '../../../api/targets-client/targets-client.hooks';
import type { GetAvailableTargetsPayload, TransformedTarget } from '../../../api/targets-client/targets-client.type';
import {
  useTimeAllocations as useTimeAllocationsHook,
  useWorkPeriodsMeasureMultiple,
} from '../../../api/work-periods-client/work-periods-client.hooks';
import {
  TimeAllocationType,
  Transformer,
  type Measure,
  type MeasureDataByEntity,
  type MeasureDataResponse,
  type UseWorkPeriodsMeasurePayloadMultiple,
} from '../../../api/work-periods-client/work-periods-client.type';
import { formatToISODate } from '../../../helpers/date-time-helpers';
import { useGlobalStore } from '../../../store/global-store/global-store';
import { useProcessAnalysisStore } from '../../../store/process-analysis-store/process-analysis-store';
import { setTimeAllocations } from '../../../store/process-analysis-store/process-analysis-store.actions';
import {
  useAvailableMeasureNames,
  useComparisonMode,
  useDateRange,
  useEntities,
  usePrimaryEntity,
  useTimeAllocation,
} from '../../../store/process-analysis-store/process-analysis-store.hooks';
import { getEntityType, splitMeasures } from '../../../helpers/process-analysis-shared/process-analysis-shared.helpers';
import { TRANSFORMER_MEASURES } from '../process-analysis.constants';
import { ComparisonMode, MultipleViewMode } from '../process-analysis.type';

/**
 * Custom hook that fetches measure comparison data.
 *
 * Returns both `isFetching` (true on background refetches) and `isLoading`
 * (true only on first paint with no cached data) so consumers can drive
 * skeleton placeholders without flashing on cached tab navigations.
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {object} The merged measure data plus loading flags
 */
const useMeasureComparisonData = (
  enabled = true,
): {
  data: MeasureDataResponse;
  entityData: MeasureDataByEntity;
  isFetching: boolean;
  isLoading: boolean;
} => {
  const {
    data: defaultMeasureData,
    entityData: defaultEntityData,
    isFetching: isDefaultFetching,
    isLoading: isDefaultLoading,
  } = useDefaultMeasureData(enabled);
  const {
    data: transformerMeasureData,
    entityData: transformerEntityData,
    isFetching: isTransformerFetching,
    isLoading: isTransformerLoading,
  } = useTransformerMeasureData(enabled);
  const {
    data: customMeasureData,
    entityData: customEntityData,
    isFetching: isCustomFetching,
    isLoading: isCustomLoading,
  } = useCustomMeasureData(enabled);

  // Merge entity data from all sources
  const mergedEntityData: MeasureDataByEntity = {};
  const allEntityData = [defaultEntityData, transformerEntityData, customEntityData];

  for (const entityData of allEntityData) {
    if (entityData) {
      for (const entityId of Object.keys(entityData)) {
        mergedEntityData[entityId] = {
          ...mergedEntityData[entityId],
          ...entityData[entityId],
        };
      }
    }
  }

  return {
    data: { ...defaultMeasureData, ...transformerMeasureData, ...customMeasureData } as MeasureDataResponse,
    entityData: mergedEntityData,
    isFetching: isDefaultFetching || isTransformerFetching || isCustomFetching,
    isLoading: isDefaultLoading || isTransformerLoading || isCustomLoading,
  };
};

/**
 * Custom hook that fetches default measure data
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {{ data: MeasureDataResponse; entityData: MeasureDataByEntity; isFetching: boolean }} Object containing the default measure data and a boolean indicating whether the data is being fetched
 */
const useDefaultMeasureData = (enabled = true) => {
  const primaryEntity = usePrimaryEntity();
  const measures = useAvailableMeasureNames();
  const groups = splitMeasures(measures, 6);

  const basePayload = usePayload(measures);

  const {
    data: firstData,
    isFetching: isFirstFetching,
    isLoading: isFirstLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: groups[0] },
    options: { enabled: enabled && !!groups[0]?.length },
  });
  const {
    data: secondData,
    isFetching: isSecondFetching,
    isLoading: isSecondLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: groups[1] },
    options: { enabled: enabled && !!groups[1]?.length },
  });
  const {
    data: thirdData,
    isFetching: isThirdFetching,
    isLoading: isThirdLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: groups[2] },
    options: { enabled: enabled && !!groups[2]?.length },
  });
  const {
    data: fourthData,
    isFetching: isFourthFetching,
    isLoading: isFourthLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: groups[3] },
    options: { enabled: enabled && !!groups[3]?.length },
  });
  const {
    data: fifthData,
    isFetching: isFifthFetching,
    isLoading: isFifthLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: groups[4] },
    options: { enabled: enabled && !!groups[4]?.length },
  });
  const {
    data: sixthData,
    isFetching: isSixthFetching,
    isLoading: isSixthLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: groups[5] },
    options: { enabled: enabled && !!groups[5]?.length },
  });

  const parsedData = (data: MeasureDataByEntity | undefined) => (data && primaryEntity ? data[primaryEntity] : null);
  const allParsedData = [
    parsedData(firstData),
    parsedData(secondData),
    parsedData(thirdData),
    parsedData(fourthData),
    parsedData(fifthData),
    parsedData(sixthData),
  ].filter(Boolean);

  const combinedData: MeasureDataResponse | null = allParsedData.length ? Object.assign({}, ...allParsedData) : null;

  // Merge all entity data
  const allRawData = [firstData, secondData, thirdData, fourthData, fifthData, sixthData];
  const mergedEntityData: MeasureDataByEntity = {};
  for (const rawData of allRawData) {
    if (rawData) {
      for (const entityId of Object.keys(rawData)) {
        mergedEntityData[entityId] = {
          ...mergedEntityData[entityId],
          ...rawData[entityId],
        };
      }
    }
  }

  const isFetching =
    isFirstFetching || isSecondFetching || isThirdFetching || isFourthFetching || isFifthFetching || isSixthFetching;
  const isLoading =
    isFirstLoading || isSecondLoading || isThirdLoading || isFourthLoading || isFifthLoading || isSixthLoading;

  return { data: combinedData, entityData: mergedEntityData, isFetching, isLoading };
};

/**
 * Custom hook that fetches transformer measure data
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {{ data: MeasureDataResponse; entityData: MeasureDataByEntity; isFetching: boolean }} Object containing the transformer measure data and a boolean indicating whether the data is being fetched
 */
const useTransformerMeasureData = (enabled = true) => {
  const primaryEntity = usePrimaryEntity();

  const basePayload = usePayload(TRANSFORMER_MEASURES);

  const { data, isFetching, isLoading } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: TRANSFORMER_MEASURES, transformer: Transformer.TimeAllocationValuesOnly },
    options: { enabled },
  });

  const parsedData = (data: MeasureDataByEntity | undefined) => (data && primaryEntity ? data[primaryEntity] : null);

  return {
    data: parsedData(data),
    entityData: data || {},
    isFetching,
    isLoading,
  };
};

/**
 * Custom hook that fetches custom measure data
 *
 * @param {boolean} [enabled=true] - Whether the hook should fetch data
 * @returns {{ data: MeasureDataResponse; entityData: MeasureDataByEntity; isFetching: boolean }} Object containing the custom measure data and a boolean indicating whether the data is being fetched
 */
const useCustomMeasureData = (enabled = true) => {
  const availableMeasures = useProcessAnalysisStore((state) => state.availableMeasures);
  const customMeasures = availableMeasures
    .filter((measure) => measure.is_custom)
    .map((measure) => measure.measure_name);
  const customGroups = splitMeasures(customMeasures, 4);

  const primaryEntity = usePrimaryEntity();

  const basePayload = usePayload(customMeasures, true);

  const {
    data: firstData,
    isFetching: isFirstFetching,
    isLoading: isFirstLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: customGroups[0], transformer: Transformer.ReturnFirstMetricOnly },
    options: { enabled: enabled && !!customGroups[0]?.length },
  });
  const {
    data: secondData,
    isFetching: isSecondFetching,
    isLoading: isSecondLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: customGroups[1], transformer: Transformer.ReturnFirstMetricOnly },
    options: { enabled: enabled && !!customGroups[1]?.length },
  });
  const {
    data: thirdData,
    isFetching: isThirdFetching,
    isLoading: isThirdLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: customGroups[2], transformer: Transformer.ReturnFirstMetricOnly },
    options: { enabled: enabled && !!customGroups[2]?.length },
  });
  const {
    data: fourthData,
    isFetching: isFourthFetching,
    isLoading: isFourthLoading,
  } = useWorkPeriodsMeasureMultiple({
    payload: { ...basePayload, measure_name: customGroups[3], transformer: Transformer.ReturnFirstMetricOnly },
    options: { enabled: enabled && !!customGroups[3]?.length },
  });

  const parsedData = (data: MeasureDataByEntity | undefined) => (data && primaryEntity ? data[primaryEntity] : null);
  const allParsedData = [
    parsedData(firstData),
    parsedData(secondData),
    parsedData(thirdData),
    parsedData(fourthData),
  ].filter(Boolean);

  const combinedData: MeasureDataResponse | null = allParsedData.length ? Object.assign({}, ...allParsedData) : null;

  // Merge all entity data
  const allRawData = [firstData, secondData, thirdData, fourthData];
  const mergedEntityData: MeasureDataByEntity = {};
  for (const rawData of allRawData) {
    if (rawData) {
      for (const entityId of Object.keys(rawData)) {
        mergedEntityData[entityId] = {
          ...mergedEntityData[entityId],
          ...rawData[entityId],
        };
      }
    }
  }

  const isFetching = isFirstFetching || isSecondFetching || isThirdFetching || isFourthFetching;
  const isLoading = isFirstLoading || isSecondLoading || isThirdLoading || isFourthLoading;

  return {
    data: combinedData,
    entityData: mergedEntityData,
    isFetching,
    isLoading,
  };
};

/**
 * Custom hook that returns a payload for fetching measure comparison data.
 * Uses Monthly time allocation only when in Multiple comparison mode AND Monthly Averages view.
 *
 * @param {Measure[]} measures - The list of measures for the payload
 * @param {boolean} [isCustom] - Whether these are custom measures
 * @return {UseWorkPeriodsMeasurePayloadMultiple} - The payload for fetching work period measures.
 */
const usePayload = (measures: Measure[], isCustom?: boolean): UseWorkPeriodsMeasurePayloadMultiple => {
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const multipleViewMode = useProcessAnalysisStore((state) => state.multipleViewMode);
  const selectedTaskType = useProcessAnalysisStore((state) => state.selectedTaskType);
  const { startDate, endDate } = useDateRange();
  const entities = useEntities();
  const comparisonMode = useComparisonMode();
  const dynamicTimeAllocation = useTimeAllocation();

  // Use Monthly allocation only when in Multiple comparison mode AND Monthly Averages view
  const isMonthlyAveragesView =
    comparisonMode === ComparisonMode.Multiple && multipleViewMode === MultipleViewMode.MonthlyAverages;
  const timeAllocation = isMonthlyAveragesView ? TimeAllocationType.Monthly : dynamicTimeAllocation;

  return {
    entity: getEntityType(activeTab),
    ids: entities,
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    measure_name: measures,
    time_allocation_type: timeAllocation,
    is_custom: isCustom,
    ...(selectedTaskType ? { task_type: selectedTaskType } : {}),
  };
};

/**
 * Custom hook that fetches available targets for measure comparison.
 *
 * @returns {TransformedTarget[]} The available targets
 */
const useMeasureComparisonTargets = (): TransformedTarget[] => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const { startDate, endDate } = useDateRange();
  const timeAllocation = useTimeAllocation();
  const activeTab = useProcessAnalysisStore((state) => state.activeTab);
  const primaryEntity = usePrimaryEntity();

  let payload: GetAvailableTargetsPayload = {
    organization_id: organizationId || '',
    start_date: formatToISODate(startDate),
    end_date: formatToISODate(endDate),
    time_allocation_type: timeAllocation,
  };

  // Add entity-specific IDs based on the active tab
  if (primaryEntity) {
    switch (activeTab) {
      case 'portfolios':
        payload.portfolio_ids = primaryEntity;
        break;
      case 'teams':
        payload.project_ids = primaryEntity;
        break;
      case 'boards':
        payload.subproject_ids = primaryEntity;
        break;
    }
  }

  const { data: availableTargets } = useAvailableTargets({ payload });

  return availableTargets?.[primaryEntity || ''] ?? [];
};

/**
 * Custom hook that fetches time allocations data and stores it in the Process Analysis store.
 *
 * @returns {object} Object containing first-paint `isLoading` (true only when
 *   no cached data is available) and background-refetch `isFetching` flags
 */
const useTimeAllocationsData = (): { isFetching: boolean; isLoading: boolean } => {
  const { startDate, endDate } = useDateRange();
  const timeAllocationType = useTimeAllocation();

  const { data, query } = useTimeAllocationsHook({
    payload: {
      start_date: formatToISODate(startDate),
      end_date: formatToISODate(endDate),
      time_allocation_type: timeAllocationType,
    },
    options: {
      enabled: !!startDate && !!endDate,
      staleTime: 1000 * 60 * 5,
    },
  });

  useEffect(() => {
    if (data) {
      setTimeAllocations(data);
    }
  }, [data]);

  return { isFetching: query.isFetching, isLoading: query.isLoading };
};

export { useMeasureComparisonData, useMeasureComparisonTargets, useTimeAllocationsData };
